import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Route, ChevronUp, ChevronDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import type { CrewMember } from "./CrewList";
import CrewRouteMap from "./CrewRouteMap";
import { companyOrSharedDemo } from "@/lib/demo";

/** Start/end depot for a crew's daily route. */
type CrewEndpoint = {
  start_address: string;
  end_address: string;
  start_lat: number | null;
  start_lng: number | null;
  end_lat: number | null;
  end_lng: number | null;
};

const EMPTY_ENDPOINT: CrewEndpoint = {
  start_address: "",
  end_address: "",
  start_lat: null,
  start_lng: null,
  end_lat: null,
  end_lng: null,
};




type Job = {
  id: string;
  customer_name: string;
  address: string;
  scheduled_date: string;
  time_window: string;
  status: string;
  assigned_to: string | null;
  notes: string | null;
  lat: number | null;
  lng: number | null;
  crew: string | null;
  sequence: number | null;
  customer_id: string | null;
  actual_duration_minutes: number | null;
  started_at: string | null;
  completed_at: string | null;
  completion_photo_path: string | null;
  skip_reason: string | null;
};

/** Estimated mow time from lawn size: ~20 min setup + 5 min per 1,000 sqft, rounded to 5. */
function estimateFromSqft(sqft: number | null | undefined, fallback: number) {
  if (!sqft || sqft <= 0) return fallback;
  return Math.max(15, Math.round((20 + (sqft / 1000) * 5) / 5) * 5);
}


/**
 * Split points into `k` geographic clusters (k-means on lat/lng) so each crew
 * gets a tight group of jobs. Returns an array of index-groups.
 */
function clusterByLocation(
  points: { lat: number; lng: number; weight?: number }[],
  k: number
): number[][] {
  const n = points.length;
  if (k <= 1 || n === 0) return n ? [points.map((_, i) => i)] : [];
  if (n <= k) return points.map((_, i) => [i]);

  // Distance in miles (equirectangular — accurate enough at city scale).
  const dist = (
    a: { lat: number; lng: number },
    b: { lat: number; lng: number }
  ) => {
    const mLat = 69.0;
    const mLng = 69.172 * Math.cos(((a.lat + b.lat) / 2) * (Math.PI / 180));
    return Math.hypot((a.lat - b.lat) * mLat, (a.lng - b.lng) * mLng);
  };

  const weightOf = (i: number) => points[i].weight ?? 1;
  const totalWeight = points.reduce((s, _, i) => s + weightOf(i), 0);

  // Seed centroids far apart (k-means++ style, deterministic).
  const centroids = [{ ...points[0] }];
  while (centroids.length < k) {
    let best = 0;
    let bestScore = -1;
    points.forEach((p, i) => {
      const score = Math.min(...centroids.map((c) => dist(p, c)));
      if (score > bestScore) {
        bestScore = score;
        best = i;
      }
    });
    centroids.push({ ...points[best] });
  }

  // Lloyd iterations to settle the cluster centres.
  let assign = new Array(n).fill(0);
  for (let iter = 0; iter < 40; iter++) {
    const next = points.map((p) => {
      let bestI = 0;
      let bestD = Infinity;
      centroids.forEach((c, i) => {
        const d = dist(p, c);
        if (d < bestD) {
          bestD = d;
          bestI = i;
        }
      });
      return bestI;
    });
    if (next.every((v, i) => v === assign[i])) break;
    assign = next;
    for (let i = 0; i < k; i++) {
      const members = points.filter((_, idx) => assign[idx] === i);
      if (members.length === 0) continue;
      centroids[i] = {
        lat: members.reduce((s, m) => s + m.lat, 0) / members.length,
        lng: members.reduce((s, m) => s + m.lng, 0) / members.length,
      };
    }
  }

  // Workload-capacitated assignment: crews get near-equal *time*, not just an
  // equal stop count. A small slack keeps clusters geographically tight.
  const target = (totalWeight / k) * 1.1;
  const load = new Array(k).fill(0);
  const groups: number[][] = Array.from({ length: k }, () => []);

  // Most "committed" jobs (biggest gap between best and second-best cluster)
  // claim their spot first; ambiguous ones fill the gaps.
  const order = points
    .map((p, i) => {
      const ds = centroids.map((c) => dist(p, c)).sort((a, b) => a - b);
      return { i, regret: (ds[1] ?? ds[0]) - ds[0] };
    })
    .sort((a, b) => b.regret - a.regret);

  order.forEach(({ i }) => {
    const ranked = centroids
      .map((c, ci) => ({ ci, d: dist(points[i], c) }))
      .sort((a, b) => a.d - b.d);
    const pick =
      ranked.find((r) => load[r.ci] + weightOf(i) <= target) ??
      ranked.slice().sort((a, b) => load[a.ci] - load[b.ci])[0];
    groups[pick.ci].push(i);
    load[pick.ci] += weightOf(i);
  });

  const recentre = () => {
    for (let c = 0; c < k; c++) {
      const g = groups[c];
      if (!g.length) continue;
      centroids[c] = {
        lat: g.reduce((s, i) => s + points[i].lat, 0) / g.length,
        lng: g.reduce((s, i) => s + points[i].lng, 0) / g.length,
      };
    }
  };
  recentre();

  // Swap refinement: exchange pairs between clusters when it tightens both
  // (keeps workloads balanced because a swap is weight-neutral in count).
  for (let pass = 0; pass < 6; pass++) {
    let improved = false;
    for (let a = 0; a < k; a++) {
      for (let b = a + 1; b < k; b++) {
        for (let x = 0; x < groups[a].length; x++) {
          for (let y = 0; y < groups[b].length; y++) {
            const i = groups[a][x];
            const j = groups[b][y];
            const before = dist(points[i], centroids[a]) + dist(points[j], centroids[b]);
            const after = dist(points[i], centroids[b]) + dist(points[j], centroids[a]);
            const wShift = Math.abs(weightOf(i) - weightOf(j));
            const balanceOk =
              Math.abs(load[a] - weightOf(i) + weightOf(j) - (load[b] - weightOf(j) + weightOf(i))) <=
              Math.abs(load[a] - load[b]) + wShift;
            if (after < before - 0.05 && balanceOk) {
              groups[a][x] = j;
              groups[b][y] = i;
              load[a] += weightOf(j) - weightOf(i);
              load[b] += weightOf(i) - weightOf(j);
              improved = true;
            }
          }
        }
      }
    }
    recentre();
    if (!improved) break;
  }

  // Move outliers to a closer cluster when that cluster still has headroom.
  for (let pass = 0; pass < 3; pass++) {
    let moved = false;
    for (let a = 0; a < k; a++) {
      for (let x = groups[a].length - 1; x >= 0; x--) {
        const i = groups[a][x];
        const here = dist(points[i], centroids[a]);
        let bestC = a;
        let bestD = here;
        for (let b = 0; b < k; b++) {
          if (b === a) continue;
          const d = dist(points[i], centroids[b]);
          if (d < bestD - 0.25 && load[b] + weightOf(i) <= target) {
            bestD = d;
            bestC = b;
          }
        }
        if (bestC !== a) {
          groups[a].splice(x, 1);
          groups[bestC].push(i);
          load[a] -= weightOf(i);
          load[bestC] += weightOf(i);
          moved = true;
        }
      }
    }
    recentre();
    if (!moved) break;
  }

  return groups.filter((g) => g.length > 0);
}



const STATUS_VARIANT: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  scheduled: "outline",
  in_progress: "default",
  completed_paid: "secondary",
  completed_unpaid: "destructive",
  completed: "secondary",
  canceled: "destructive",
};

const STATUS_LABEL: Record<string, string> = {
  scheduled: "Scheduled",
  in_progress: "In progress",
  completed_paid: "Complete — paid",
  completed_unpaid: "Complete — not paid",
  canceled: "Canceled",
};

// Legacy value kept only for display of older records
const LEGACY_STATUS_LABEL: Record<string, string> = { completed: "Completed" };


type DueCustomer = {
  id: string;
  name: string;
  address: string;
  city: string | null;
  zip: string | null;
  frequency: string | null;
  service_day: string | null;
  lat: number | null;
  lng: number | null;
  lawn_sqft: number | null;
  est_duration_minutes: number | null;
};

const WEEKDAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

function todayISO() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function formatDay(date: string) {
  const d = new Date(`${date}T00:00:00`);
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

/** Format minutes as "6h 15m" (or "45m" under an hour). */
function fmtHrs(mins: number) {
  const m = Math.round(mins);
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return h > 0 ? `${h}h ${rest}m` : `${rest}m`;
}

/** Mowing season: March 15 → October 31. */
const SEASON_START = { month: 3, day: 15 };
const SEASON_END = { month: 10, day: 31 };

function seasonBounds(isoDate: string) {
  const year = Number(isoDate.slice(0, 4));
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    start: `${year}-${pad(SEASON_START.month)}-${pad(SEASON_START.day)}`,
    end: `${year}-${pad(SEASON_END.month)}-${pad(SEASON_END.day)}`,
  };
}

function inSeason(isoDate: string) {
  const { start, end } = seasonBounds(isoDate);
  return isoDate >= start && isoDate <= end;
}

function addDaysISO(isoDate: string, days: number) {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() + days);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

/** Whether a customer's recurring service falls on the given ISO date. */
function isDueOn(c: DueCustomer, isoDate: string) {
  if (!c.service_day) return false;
  if (!inSeason(isoDate)) return false;
  const d = new Date(`${isoDate}T00:00:00`);
  const weekday = WEEKDAYS[d.getDay()];
  const target = c.service_day.trim().toLowerCase();
  const matchesDay = weekday === target || weekday.startsWith(target.slice(0, 3));
  if (!matchesDay) return false;

  const freq = (c.frequency ?? "weekly").trim().toLowerCase();
  if (freq.includes("one") || freq.includes("once") || freq.includes("single")) return false;
  if (freq.includes("bi") || freq.includes("every other") || freq.includes("2 week")) {
    // Every other week, anchored to epoch week parity.
    const weekIndex = Math.floor(d.getTime() / (7 * 24 * 60 * 60 * 1000));
    return weekIndex % 2 === 0;
  }
  if (freq.includes("month")) {
    // First matching weekday of the month.
    return d.getDate() <= 7;
  }
  return true; // weekly / unknown
}

/** True for a one-time / single-visit customer. */
function isOneTime(c: DueCustomer) {
  const freq = (c.frequency ?? "").trim().toLowerCase();
  return freq.includes("one") || freq.includes("once") || freq.includes("single");
}

/** Expected days between visits for a recurring customer. */
function intervalDays(c: DueCustomer) {
  const freq = (c.frequency ?? "weekly").trim().toLowerCase();
  if (freq.includes("bi") || freq.includes("every other") || freq.includes("2 week")) return 14;
  if (freq.includes("month")) return 30;
  return 7;
}

/** Straight-line distance in miles. */
function milesBetween(aLat: number, aLng: number, bLat: number, bLng: number) {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 3958.8 * Math.asin(Math.sqrt(s));
}

/** How close a catch-up stop must be to an existing stop to be worth adding. */
const CATCH_UP_RADIUS_MILES = 1.5;
const CATCH_UP_MAX_PER_DAY = 5;
const CATCH_UP_NOTE = "Catch-up: missed service";





export default function JobSchedule({
  companyId,
  isOwner,
  crew,
  refreshKey = 0,
  onRefreshCrew,
}: {
  companyId: string;
  isOwner: boolean;
  crew: CrewMember[];
  refreshKey?: number;
  onRefreshCrew?: () => void | Promise<void>;
}) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [customers, setCustomers] = useState<DueCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(todayISO());
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [crewCount, setCrewCount] = useState("2");
  const [optimizing, setOptimizing] = useState(false);
  const [optimizingCrew, setOptimizingCrew] = useState<string | null>(null);
  const [splitting, setSplitting] = useState(false);
  const [crewSplit, setCrewSplit] = useState<string[]>([]);
  const [optError, setOptError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  /** Per-crew depot addresses (start / end of the route). */
  const [endpoints, setEndpoints] = useState<Record<string, CrewEndpoint>>({});

  const [savingEndpoint, setSavingEndpoint] = useState<string | null>(null);
  const [routeInfo, setRouteInfo] = useState<{
    optimized_at: string | null;
    stops: number;
    total_distance_meters: number | null;
    total_duration_minutes: number | null;
  } | null>(null);
  /** Optimized drive time + distance per crew for the selected date. */
  const [driveByCrew, setDriveByCrew] = useState<
    Record<string, { minutes: number | null; miles: number | null }>
  >({});
  /** Company target work-day hours keyed by day of week (0 = Sunday). */
  const [targetHours, setTargetHours] = useState<Record<number, number>>({});



  const [form, setForm] = useState({
    customer_name: "",
    address: "",
    scheduled_date: new Date().toISOString().slice(0, 10),
    time_window: "morning",
    assigned_to: "unassigned",
    notes: "",
  });

  const loadJobs = async () => {
    const { data, error } = await supabase
      .from("jobs")
      .select(
        "id, customer_name, address, scheduled_date, time_window, status, assigned_to, notes, lat, lng, crew, sequence, customer_id, actual_duration_minutes, started_at, completed_at, completion_photo_path, skip_reason"
      )
      .eq("company_id", companyId)
      .order("scheduled_date", { ascending: true })
      .order("time_window", { ascending: true });
    if (error) toast.error("Could not load jobs");
    setJobs((data ?? []) as Job[]);
    setLoading(false);
  };

  const loadCustomers = async () => {
    const { data, error } = await supabase
      .from("customers")
      .select(
        "id, name, address, city, zip, frequency, service_day, lat, lng, lawn_sqft, est_duration_minutes"
      )
      .or(companyOrSharedDemo(companyId))
      .order("name", { ascending: true });
    if (error) {
      toast.error("Could not load customers");
      return;
    }
    setCustomers((data ?? []) as DueCustomer[]);
  };

  const loadEndpoints = async () => {
    const { data } = await supabase
      .from("crews")
      .select("name, start_address, end_address, start_lat, start_lng, end_lat, end_lng")
      .eq("company_id", companyId);
    const map: Record<string, CrewEndpoint> = {};
    (data ?? []).forEach((c) => {
      map[c.name] = {
        start_address: c.start_address ?? "",
        end_address: c.end_address ?? "",
        start_lat: c.start_lat ?? null,
        start_lng: c.start_lng ?? null,
        end_lat: c.end_lat ?? null,
        end_lng: c.end_lng ?? null,
      };
    });
    setEndpoints(map);
  };

  const loadTargets = async () => {
    const { data } = await supabase
      .from("company_availability")
      .select("day_of_week, target_hours, is_available")
      .eq("company_id", companyId);
    const map: Record<number, number> = {};
    (data ?? []).forEach((r) => {
      map[r.day_of_week] = r.is_available ? Number(r.target_hours ?? 8) : 0;
    });
    setTargetHours(map);
  };



  /** Save a crew's start/end address, creating the crew record if needed. */
  const saveEndpoint = async (
    name: string,
    field: "start_address" | "end_address",
    value: string
  ) => {
    setSavingEndpoint(`${name}:${field}`);
    const trimmed = value.trim() || null;
    const { error } = await supabase
      .from("crews")
      .upsert(
        { company_id: companyId, name, [field]: trimmed },
        { onConflict: "company_id,name" }
      );
    setSavingEndpoint(null);
    if (error) {
      toast.error("Could not save address");
      return;
    }
    toast.success(`${name} ${field === "start_address" ? "start" : "end"} address saved`);
  };

  useEffect(() => {
    loadJobs();
    loadCustomers();
    loadEndpoints();
    loadTargets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, refreshKey]);

  const visible = useMemo(
    () => jobs.filter((j) => j.scheduled_date === dateFilter),
    [jobs, dateFilter]
  );

  /**
   * Keep the crew selector in sync with how the day is actually split, so the
   * button never says "Split across 2 crews" while 3 crews are assigned.
   */
  useEffect(() => {
    const assigned = new Set(
      visible
        .map((j) => j.crew)
        .filter((c): c is string => !!c && /^Crew \d+$/.test(c))
    );
    if (assigned.size > 0) setCrewCount(String(assigned.size));
  }, [visible]);


  /**
   * Per-customer job time: the real average once we have 2+ completed mows,
   * otherwise an estimate from lawn square footage.
   */
  const durationByCustomer = useMemo(() => {
    const actuals = new Map<string, number[]>();
    jobs.forEach((j) => {
      if (!j.customer_id || !j.actual_duration_minutes) return;
      actuals.set(j.customer_id, [...(actuals.get(j.customer_id) ?? []), j.actual_duration_minutes]);
    });
    const map = new Map<string, { minutes: number; actual: boolean }>();
    customers.forEach((c) => {
      const list = actuals.get(c.id) ?? [];
      if (list.length >= 2) {
        map.set(c.id, {
          minutes: Math.round(list.reduce((a, b) => a + b, 0) / list.length),
          actual: true,
        });
      } else {
        map.set(c.id, {
          minutes: estimateFromSqft(c.lawn_sqft, c.est_duration_minutes ?? 45),
          actual: false,
        });
      }
    });
    return map;
  }, [jobs, customers]);


  /** One column per crew for the selected date, stops ordered by route sequence. */
  const crewColumns = useMemo(() => {
    const map = new Map<string, Job[]>();
    visible.forEach((j) => {
      const key = j.crew ?? "Unassigned";
      map.set(key, [...(map.get(key) ?? []), j]);
    });
    const cols = Array.from(map.entries()).map(([name, list]) => ({
      name,
      jobs: [...list].sort(
        (a, b) => (a.sequence ?? 999) - (b.sequence ?? 999) ||
          a.customer_name.localeCompare(b.customer_name)
      ),
    }));
    cols.sort((a, b) =>
      a.name === "Unassigned" ? 1 : b.name === "Unassigned" ? -1 : a.name.localeCompare(b.name)
    );
    return cols;
  }, [visible]);

  /** Company target work-day length (minutes) for the selected date. */
  const dayTargetMinutes = useMemo(() => {
    const dow = new Date(`${dateFilter}T00:00:00`).getDay();
    const hours = targetHours[dow];
    return hours == null || hours <= 0 ? null : hours * 60;
  }, [targetHours, dateFilter]);


  const maxStops = useMemo(
    () => crewColumns.reduce((m, c) => Math.max(m, c.jobs.length), 0),
    [crewColumns]
  );

  /** Last completed service date per customer. */
  const lastServiced = useMemo(() => {
    const map = new Map<string, string>();
    jobs.forEach((j) => {
      if (!j.customer_id || !j.status?.startsWith("complete")) return;
      const prev = map.get(j.customer_id);
      if (!prev || j.scheduled_date > prev) map.set(j.customer_id, j.scheduled_date);
    });
    return map;
  }, [jobs]);

  /** Customers with an open job on this date or later (already covered). */
  const upcomingCustomerIds = useMemo(() => {
    const today = todayISO();
    return new Set(
      jobs
        .filter((j) => j.customer_id && j.status === "scheduled" && j.scheduled_date >= today)
        .map((j) => j.customer_id as string)
    );
  }, [jobs]);

  /** One-time customers that have never been serviced and match this day. */
  const oneTimeDue = useMemo(() => {
    if (!inSeason(dateFilter)) return [] as DueCustomer[];
    const weekday = WEEKDAYS[new Date(`${dateFilter}T00:00:00`).getDay()];
    const everHadJob = new Set(jobs.map((j) => j.customer_id).filter(Boolean) as string[]);
    return customers.filter((c) => {
      if (!isOneTime(c)) return false;
      if (everHadJob.has(c.id) && !upcomingCustomerIds.has(c.id)) return false;
      if (lastServiced.has(c.id)) return false;
      const target = (c.service_day ?? "").trim().toLowerCase();
      if (!target) return false;
      return weekday === target || weekday.startsWith(target.slice(0, 3));
    });
  }, [customers, jobs, dateFilter, lastServiced, upcomingCustomerIds]);

  const dueCustomers = useMemo(
    () => [...customers.filter((c) => isDueOn(c, dateFilter)), ...oneTimeDue],
    [customers, dateFilter, oneTimeDue]
  );

  const scheduledKeys = useMemo(
    () => new Set(visible.map((j) => `${j.customer_name}|${j.address}`.toLowerCase())),
    [visible]
  );

  const fullAddress = (c: DueCustomer) =>
    [c.address, c.city, c.zip].filter(Boolean).join(", ");

  /** Past days are an immutable historical record — never auto-modified. */
  const isPastDate = dateFilter < todayISO();

  /**
   * Overdue (missed) recurring customers: past their expected interval, with no
   * upcoming stop already booked. They get slotted into whichever day's route
   * already passes close by.
   */
  const catchUpCustomers = useMemo(() => {
    if (isPastDate || !inSeason(dateFilter)) return [] as DueCustomer[];
    const dueIds = new Set(dueCustomers.map((c) => c.id));
    const anchors = [...dueCustomers, ...visible]
      .map((x) => ({ lat: (x as { lat: number | null }).lat, lng: (x as { lng: number | null }).lng }))
      .filter((p) => p.lat != null && p.lng != null) as { lat: number; lng: number }[];
    if (anchors.length === 0) return [];

    const ref = new Date(`${dateFilter}T00:00:00`).getTime();
    const overdue = customers
      .filter((c) => !isOneTime(c) && !dueIds.has(c.id) && !upcomingCustomerIds.has(c.id))
      .filter((c) => c.lat != null && c.lng != null)
      .map((c) => {
        const last = lastServiced.get(c.id);
        if (!last) return null;
        const days = Math.round((ref - new Date(`${last}T00:00:00`).getTime()) / 86400000);
        const overdueBy = days - intervalDays(c);
        if (overdueBy < 3) return null;
        const dist = Math.min(
          ...anchors.map((a) => milesBetween(c.lat as number, c.lng as number, a.lat, a.lng))
        );
        if (dist > CATCH_UP_RADIUS_MILES) return null;
        return { customer: c, dist, overdueBy };
      })
      .filter(Boolean) as { customer: DueCustomer; dist: number; overdueBy: number }[];

    return overdue
      .sort((a, b) => b.overdueBy - a.overdueBy || a.dist - b.dist)
      .slice(0, CATCH_UP_MAX_PER_DAY)
      .map((o) => o.customer);
  }, [customers, dueCustomers, visible, lastServiced, upcomingCustomerIds, dateFilter, isPastDate]);

  /**
   * Automatically remove untouched "scheduled" jobs whose customer is no longer
   * due on this date (service day / frequency changed, or customer removed).
   * Catch-up stops are left alone. Only applies to today and future dates.
   */
  const pruningRef = useRef(false);
  useEffect(() => {
    if (!isOwner || loading || pruningRef.current || isPastDate) return;
    if (customers.length === 0) return;

    const keepIds = new Set(dueCustomers.map((c) => c.id));
    const stale = visible.filter(
      (j) =>
        j.status === "scheduled" &&
        j.customer_id &&
        !keepIds.has(j.customer_id) &&
        !(j.notes ?? "").startsWith(CATCH_UP_NOTE)
    );
    if (stale.length === 0) return;
    const ids = stale.map((j) => j.id);
    pruningRef.current = true;
    (async () => {
      await supabase.from("route_stops").delete().in("job_id", ids);
      const { error } = await supabase.from("jobs").delete().in("id", ids);
      pruningRef.current = false;
      if (error) return;
      toast.info(
        `Removed ${ids.length} stop${ids.length === 1 ? "" : "s"} no longer due on this date`
      );
      await loadJobs();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, customers, dueCustomers, dateFilter, loading, isOwner]);

  /**
   * Customers are locked in: every customer due on this date (recurring or a
   * pending one-time visit) is automatically on the schedule, plus nearby
   * missed services worth catching up on. Past days stay untouched.
   */
  const autoRef = useRef(false);
  useEffect(() => {
    if (!isOwner || loading || autoRef.current || isPastDate) return;
    if (customers.length === 0) return;
    const isScheduled = (c: DueCustomer) =>
      scheduledKeys.has(`${c.name}|${fullAddress(c)}`.toLowerCase());
    const missing = dueCustomers.filter((c) => !isScheduled(c));
    const catchUps = catchUpCustomers.filter((c) => !isScheduled(c));
    if (missing.length === 0 && catchUps.length === 0) return;
    autoRef.current = true;
    (async () => {
      const row = (c: DueCustomer, note: string | null) => ({
        company_id: companyId,
        customer_name: c.name,
        address: fullAddress(c),
        scheduled_date: dateFilter,
        time_window: "morning",
        customer_id: c.id,
        lat: c.lat,
        lng: c.lng,
        notes: note,
      });
      const { error } = await supabase
        .from("jobs")
        .insert([
          ...missing.map((c) => row(c, null)),
          ...catchUps.map((c) => row(c, CATCH_UP_NOTE)),
        ]);
      autoRef.current = false;
      if (error) return;
      if (catchUps.length)
        toast.info(
          `Added ${catchUps.length} nearby catch-up stop${catchUps.length === 1 ? "" : "s"}`
        );
      await loadJobs();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, dueCustomers, catchUpCustomers, visible, dateFilter, loading, isOwner, isPastDate]);


  const refreshLists = async () => {
    setRefreshing(true);
    await Promise.all([loadJobs(), loadCustomers(), loadEndpoints(), loadTargets(), onRefreshCrew?.()]);
    setRefreshing(false);
  };



  const crewName = (id: string | null) => {
    if (!id) return "Unassigned";
    const m = crew.find((c) => c.id === id);
    return m?.full_name || m?.email || "Crew member";
  };

  const createJob = async () => {
    if (!form.customer_name.trim() || !form.address.trim()) {
      toast.error("Customer name and address are required");
      return;
    }
    setSaving(true);
    const { error } = await supabase.from("jobs").insert({
      company_id: companyId,
      customer_name: form.customer_name.trim(),
      address: form.address.trim(),
      scheduled_date: form.scheduled_date,
      time_window: form.time_window,
      assigned_to: form.assigned_to === "unassigned" ? null : form.assigned_to,
      notes: form.notes.trim() || null,
    });
    setSaving(false);
    if (error) {
      toast.error("Could not create job");
      return;
    }
    toast.success("Job scheduled");
    setOpen(false);
    setForm({ ...form, customer_name: "", address: "", notes: "" });
    loadJobs();
  };

  const updateJob = async (id: string, patch: Partial<Job>) => {
    const dbPatch: Record<string, unknown> = { ...patch };
    // Track real work time so we can switch from estimates to averages.
    if (patch.status === "in_progress") {
      dbPatch.started_at = new Date().toISOString();
      dbPatch.completed_at = null;
      dbPatch.actual_duration_minutes = null;
    } else if (patch.status?.startsWith("completed")) {
      const { data: existing } = await supabase
        .from("jobs")
        .select("started_at")
        .eq("id", id)
        .maybeSingle();
      const now = new Date();
      dbPatch.completed_at = now.toISOString();
      if (existing?.started_at) {
        const mins = Math.round(
          (now.getTime() - new Date(existing.started_at).getTime()) / 60000
        );
        if (mins > 0 && mins < 480) dbPatch.actual_duration_minutes = mins;
      }
    }
    setJobs((prev) =>
      prev.map((j) =>
        j.id === id
          ? {
              ...j,
              ...patch,
              actual_duration_minutes:
                (dbPatch.actual_duration_minutes as number | null | undefined) ??
                j.actual_duration_minutes,
            }
          : j
      )
    );
    const { error } = await supabase.from("jobs").update(dbPatch).eq("id", id);
    if (error) {
      toast.error("Could not update job");
      loadJobs();
    }
  };

  /** Owner override: move a stop to another crew, dropped at the end of that route. */
  const moveStopToCrew = async (job: Job, crewTo: string) => {
    const target = crewTo === "Unassigned" ? null : crewTo;
    const lastSeq = visible
      .filter((j) => (j.crew ?? "Unassigned") === crewTo && j.id !== job.id)
      .reduce((m, j) => Math.max(m, j.sequence ?? 0), 0);
    setJobs((prev) =>
      prev.map((j) => (j.id === job.id ? { ...j, crew: target, sequence: lastSeq + 1 } : j))
    );
    const { error } = await supabase
      .from("jobs")
      .update({ crew: target, sequence: lastSeq + 1 })
      .eq("id", job.id);
    if (error) {
      toast.error("Could not move that stop");
      loadJobs();
      return;
    }
    toast.success(`Moved ${job.customer_name} to ${crewTo}`);
  };

  /** Owner override: nudge a stop up or down inside its crew's route. */
  const reorderStop = async (job: Job, direction: -1 | 1) => {
    const column = crewColumns.find((c) => c.name === (job.crew ?? "Unassigned"));
    if (!column) return;
    const idx = column.jobs.findIndex((j) => j.id === job.id);
    const swapWith = column.jobs[idx + direction];
    if (!swapWith) return;
    const ordered = [...column.jobs];
    ordered[idx] = swapWith;
    ordered[idx + direction] = job;
    const patches = ordered.map((j, i) => ({ id: j.id, sequence: i + 1 }));
    setJobs((prev) =>
      prev.map((j) => {
        const hit = patches.find((p) => p.id === j.id);
        return hit ? { ...j, sequence: hit.sequence } : j;
      })
    );
    const results = await Promise.all(
      patches.map((pch) => supabase.from("jobs").update({ sequence: pch.sequence }).eq("id", pch.id))
    );
    if (results.some((r) => r.error)) {
      toast.error("Could not reorder that route");
      loadJobs();
    }
  };

  /** Skip a stop with a reason, optionally pushing it to another day. */
  const skipStop = async (job: Job) => {
    const reason = window.prompt(
      `Why is ${job.customer_name} being skipped? (gate locked, dog out, too wet…)`
    );
    if (reason === null) return;
    const push = window.confirm("Reschedule this stop for the next day? Cancel = skip it entirely.");
    const patch: Record<string, unknown> = {
      skip_reason: reason.trim() || "Skipped",
      notes: [job.notes, `Skipped: ${reason.trim() || "no reason given"}`].filter(Boolean).join(" · "),
    };
    if (push) {
      patch.scheduled_date = addDaysISO(job.scheduled_date, 1);
      patch.status = "scheduled";
      patch.sequence = null;
    } else {
      patch.status = "canceled";
    }
    const { error } = await supabase.from("jobs").update(patch).eq("id", job.id);
    if (error) {
      toast.error("Could not skip that stop");
      return;
    }
    toast.success(push ? "Pushed to tomorrow" : "Stop skipped");
    loadJobs();
  };

  /** Rain day: push every unfinished stop on this date forward. */
  const pushDay = async (days: number) => {
    const movable = visible.filter((j) => j.status === "scheduled");
    if (!movable.length) {
      toast.info("Nothing left to push on this day");
      return;
    }
    const to = addDaysISO(dateFilter, days);
    if (!window.confirm(`Move ${movable.length} unfinished stop(s) to ${to}?`)) return;
    const { error } = await supabase
      .from("jobs")
      .update({ scheduled_date: to, sequence: null })
      .in(
        "id",
        movable.map((j) => j.id)
      );
    if (error) {
      toast.error("Could not push the day");
      return;
    }
    toast.success(`Pushed ${movable.length} stop(s) to ${to}`);
    loadJobs();
  };

  // Live crew progress: the office sees Start/Complete taps without refreshing.
  useEffect(() => {
    const channel = supabase
      .channel(`jobs-live-${companyId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "jobs", filter: `company_id=eq.${companyId}` },
        () => loadJobs()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  /** Open a crew's proof-of-service photo through a short-lived signed URL. */
  const openProof = async (job: Job) => {
    if (!job.completion_photo_path) return;
    const { data, error } = await supabase.storage
      .from("job-photos")
      .createSignedUrl(job.completion_photo_path, 300);
    if (error || !data?.signedUrl) {
      toast.error("Could not open that photo");
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  };

  const loadRoute = async () => {
    const { data } = await supabase
      .from("routes")
      .select("crew_name, optimized_at, total_distance_meters, total_duration_minutes, route_stops(id)")
      .eq("company_id", companyId)
      .eq("route_date", dateFilter);
    const rows = data ?? [];
    if (!rows.length) {
      setRouteInfo(null);
      return;
    }
    setRouteInfo({
      optimized_at:
        rows
          .map((r) => r.optimized_at)
          .filter(Boolean)
          .sort()
          .slice(-1)[0] ?? null,
      stops: rows.reduce(
        (s, r) => s + ((r.route_stops as { id: string }[] | null)?.length ?? 0),
        0
      ),
      total_distance_meters: rows.reduce(
        (s, r) => s + Number(r.total_distance_meters ?? 0),
        0
      ),
      total_duration_minutes: rows.reduce(
        (s, r) => s + Number(r.total_duration_minutes ?? 0),
        0
      ),
    });
  };

  /** Drive time + distance per crew for the selected date (from optimized routes). */
  const loadAllRoutes = async () => {
    const { data } = await supabase
      .from("routes")
      .select("crew_name, total_distance_meters, total_duration_minutes")
      .eq("company_id", companyId)
      .eq("route_date", dateFilter);
    const map: Record<string, { minutes: number | null; miles: number | null }> = {};
    (data ?? []).forEach((r) => {
      if (!r.crew_name) return;
      map[r.crew_name] = {
        minutes: r.total_duration_minutes != null ? Number(r.total_duration_minutes) : null,
        miles:
          r.total_distance_meters != null ? Number(r.total_distance_meters) / 1609.34 : null,
      };
    });
    setDriveByCrew(map);
  };

  useEffect(() => {
    loadRoute();
    loadAllRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, dateFilter, refreshKey]);


  const optimizeRoute = async (crewName: string) => {
    const target = crewName;
    setOptimizing(true);
    setOptimizingCrew(target);
    setOptError(null);
    const { data, error } = await supabase.functions.invoke("optimize-route", {
      body: { company_id: companyId, route_date: dateFilter, crew_name: target },
    });
    setOptimizingCrew(null);
    setOptimizing(false);

    const payloadError =
      (data as { error?: unknown } | null)?.error ??
      (error ? ((await error.context?.json?.().catch(() => null))?.error ?? error.message) : null);

    if (payloadError) {
      const msg =
        typeof payloadError === "string" ? payloadError : JSON.stringify(payloadError);
      setOptError(msg);
      toast.error("Route optimization failed");
      return;
    }

    const skipped = (data as { skipped_missing_coordinates?: string[] })
      ?.skipped_missing_coordinates ?? [];
    const catchup = (data as { catchup_added?: string[] })?.catchup_added ?? [];
    toast.success(
      `Route optimized — ${(data as { stops: number }).stops} stop${
        (data as { stops: number }).stops === 1 ? "" : "s"
      }${catchup.length ? ` · ${catchup.length} catch-up added` : ""}`
    );
    if (catchup.length) {
      toast.message("Missed services added to fill the day", {
        description: catchup.join(", "),
      });
    }
    if (skipped.length) {
      setOptError(`Skipped (no coordinates yet): ${skipped.join(", ")}`);
    }

    await Promise.all([loadRoute(), loadAllRoutes(), loadJobs()]);
  };


  /** Crew names for the chosen split, e.g. ["Crew 1", "Crew 2"]. */
  const activeCrewNames = useMemo(
    () => Array.from({ length: Number(crewCount) || 1 }, (_, i) => `Crew ${i + 1}`),
    [crewCount]
  );

  /**
   * Schedule all due customers, split them geographically into one group per
   * crew, then optimize each crew's route.
   */
  const splitAcrossCrews = async () => {
    setSplitting(true);
    setOptError(null);

    const pending = dueCustomers.filter(
      (c) => !scheduledKeys.has(`${c.name}|${fullAddress(c)}`.toLowerCase())
    );
    if (pending.length > 0) {
      const { error } = await supabase.from("jobs").insert(
        pending.map((c) => ({
          company_id: companyId,
          customer_name: c.name,
          address: fullAddress(c),
          scheduled_date: dateFilter,
          time_window: "morning",
          customer_id: c.id,
          lat: c.lat,
          lng: c.lng,
        }))
      );
      if (error) {
        setSplitting(false);
        toast.error("Could not schedule jobs");
        return;
      }
    }

    const { data: dayJobs, error: loadErr } = await supabase
      .from("jobs")
      .select("id, customer_name, lat, lng, est_duration_minutes")
      .eq("company_id", companyId)
      .eq("scheduled_date", dateFilter);
    if (loadErr || !dayJobs?.length) {
      setSplitting(false);
      toast.error("No jobs to group for this date");
      return;
    }

    const withCoords = dayJobs.filter((j) => j.lat != null && j.lng != null);
    const withoutCoords = dayJobs.filter((j) => j.lat == null || j.lng == null);
    if (withCoords.length === 0) {
      setSplitting(false);
      setOptError(
        "No jobs have coordinates yet — save customer addresses so they can be geocoded."
      );
      return;
    }

    // Cluster geographically, weighted by mow time so crews get even days.
    const groups = clusterByLocation(
      withCoords.map((j) => ({
        lat: j.lat as number,
        lng: j.lng as number,
        weight: j.est_duration_minutes ?? 45,
      })),
      activeCrewNames.length
    );

    // Keep crew↔area pairing stable across days: order clusters west → east.
    groups.sort((a, b) => {
      const avg = (g: number[]) =>
        g.reduce((s, i) => s + (withCoords[i].lng as number), 0) / g.length;
      return avg(a) - avg(b);
    });


    // Assign each cluster to a crew.
    for (let i = 0; i < groups.length; i++) {
      const crewName = activeCrewNames[i % activeCrewNames.length];
      const ids = groups[i].map((idx) => withCoords[idx].id);
      await supabase.from("jobs").update({ crew: crewName }).in("id", ids);
    }
    // Anything without coordinates goes to the first crew so it isn't lost.
    if (withoutCoords.length) {
      await supabase
        .from("jobs")
        .update({ crew: activeCrewNames[0] })
        .in(
          "id",
          withoutCoords.map((j) => j.id)
        );
    }

    // Optimize each crew's route.
    const results: string[] = [];
    for (let i = 0; i < groups.length; i++) {
      const crewName = activeCrewNames[i % activeCrewNames.length];
      const { data, error } = await supabase.functions.invoke("optimize-route", {
        body: { company_id: companyId, route_date: dateFilter, crew_name: crewName },
      });
      const payloadError =
        (data as { error?: string } | null)?.error ?? (error ? error.message : null);
      if (payloadError) {
        results.push(`${crewName}: ${payloadError}`);
      } else {
        results.push(`${crewName}: ${(data as { stops: number }).stops} stops`);
      }
    }

    setSplitting(false);
    setCrewSplit(results);
    toast.success(`Grouped jobs across ${groups.length} crew${groups.length === 1 ? "" : "s"}`);
    await Promise.all([loadJobs(), loadRoute(), loadAllRoutes()]);
  };



  return (

    <Card>
      <CardHeader className="space-y-3">
        <div className="min-w-0 space-y-2 sm:flex sm:items-center sm:justify-between sm:space-y-0">
          <CardTitle className="text-base">Job schedule</CardTitle>
          <div className="grid min-w-0 w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-center">
            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value || todayISO())}
              className="col-span-2 h-9 min-w-0 max-w-full sm:w-[170px] sm:flex-none"
              aria-label="Filter schedule by date"
            />

            {isOwner && (
              <Button size="sm" variant="outline" className="min-w-0 px-2 sm:flex-none sm:px-3" onClick={() => pushDay(1)}>
                Rain day → push
              </Button>
            )}

            {isOwner && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="w-full min-w-0 px-2 sm:w-auto sm:flex-none sm:px-3">Add job</Button>

                </DialogTrigger>
                <DialogContent className="bg-background">
                  <DialogHeader>
                    <DialogTitle>Schedule a job</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="customer">Customer name</Label>
                      <Input
                        id="customer"
                        value={form.customer_name}
                        onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="address">Address</Label>
                      <Input
                        id="address"
                        value={form.address}
                        onChange={(e) => setForm({ ...form, address: e.target.value })}
                      />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="date">Date</Label>
                        <Input
                          id="date"
                          type="date"
                          value={form.scheduled_date}
                          onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Time window</Label>
                        <Select
                          value={form.time_window}
                          onValueChange={(v) => setForm({ ...form, time_window: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-popover">
                            <SelectItem value="morning">Morning</SelectItem>
                            <SelectItem value="afternoon">Afternoon</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Assign to</Label>
                      <Select
                        value={form.assigned_to}
                        onValueChange={(v) => setForm({ ...form, assigned_to: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-popover">
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {crew.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.full_name || c.email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createJob} disabled={saving}>
                      {saving ? "Saving…" : "Schedule job"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {isPastDate && (
          <p className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
            Archived day — stops and routes stay saved even if customers, crews or service days
            change. You can still update each job's status to track payments.
          </p>
        )}

        {!isPastDate && (
        <div className="space-y-2 rounded-md border bg-muted/30 p-3">

          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 text-sm font-semibold">
              Locked in — {formatDay(dateFilter)}{" "}
              <span className="font-normal text-muted-foreground">({dueCustomers.length})</span>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-xs font-normal"
                onClick={refreshLists}
                disabled={refreshing}
              >
                {refreshing ? "Refreshing…" : "Refresh"}
              </Button>
            </h3>
            {isOwner && (
              <div className="flex flex-wrap items-center gap-2">
                <Select value={crewCount} onValueChange={setCrewCount}>
                  <SelectTrigger className="h-8 w-[120px] text-xs" aria-label="Number of crews">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n} crew{n === 1 ? "" : "s"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={splitAcrossCrews}
                  disabled={optimizing || saving || splitting}
                  title={`Split the day's jobs into ${crewCount} geographic groups`}
                >
                  {splitting
                    ? "Grouping…"
                    : `Split across ${crewCount} crew${crewCount === "1" ? "" : "s"}`}
                </Button>
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {routeInfo?.optimized_at
              ? `Routes last optimized ${new Date(
                  routeInfo.optimized_at
                ).toLocaleString()} — ${routeInfo.stops} stop${
                  routeInfo.stops === 1 ? "" : "s"
                }${
                  routeInfo.total_distance_meters != null
                    ? `, ${(Number(routeInfo.total_distance_meters) / 1609.34).toFixed(1)} mi`
                    : ""
                }${
                  routeInfo.total_duration_minutes != null
                    ? `, ~${Math.round(Number(routeInfo.total_duration_minutes))} min`
                    : ""
                }`
              : `No optimized routes yet for ${formatDay(dateFilter)}.`}
          </p>
          {crewSplit.length > 0 && (
            <p className="rounded border bg-background p-2 text-xs text-muted-foreground">
              Crew split — {crewSplit.join(" · ")}
            </p>
          )}
          {optError && (
            <p className="rounded border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
              {optError}
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            {inSeason(dateFilter)
              ? "Every customer due today is automatically on the schedule — no manual scheduling needed."
              : "Outside the mowing season (Mar 15 – Oct 31) — no recurring service is scheduled."}
          </p>
        </div>
        )}



        {loading && <p className="text-sm text-muted-foreground">Loading schedule…</p>}
        {!loading && crewColumns.length === 0 && (
          <p className="text-sm text-muted-foreground">No jobs scheduled for this date.</p>
        )}

        {!loading && crewColumns.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">{formatDay(dateFilter)}</h3>
            <div className="overflow-x-auto rounded border bg-background">
              <table className="w-full border-collapse text-xs">
                <thead className="bg-muted">
                  <tr className="[&>th]:border-r [&>th]:border-border [&>th]:px-2 [&>th]:py-1 [&>th]:text-left [&>th]:font-semibold [&>th:last-child]:border-r-0">
                    <th className="w-10">#</th>
                    {crewColumns.map((c) => (
                      <th key={c.name}>
                        <div className="flex items-center gap-2">
                          <span>
                            {c.name}{" "}
                            <span className="font-normal text-muted-foreground">
                              ({c.jobs.length})
                            </span>
                          </span>
                          {isOwner && c.name !== "Unassigned" && (
                            <Button
                              size="sm"
                              variant="default"
                              className="h-7 shrink-0 gap-1 px-2.5 text-xs font-semibold shadow-sm"
                              disabled={optimizing || splitting}
                              onClick={() => optimizeRoute(c.name)}
                            >
                              <Route className="h-3 w-3" />
                              {optimizingCrew === c.name ? "Optimizing…" : "Optimize route"}
                            </Button>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-border bg-primary/5 [&>td]:border-r [&>td]:border-border [&>td]:px-2 [&>td]:py-1 [&>td]:align-top [&>td:last-child]:border-r-0">
                    <td className="font-semibold text-muted-foreground">Start</td>
                    {crewColumns.map((c) => (
                      <td key={c.name}>
                        {isOwner && c.name !== "Unassigned" ? (
                          <Input
                            className="h-7 text-xs"
                            placeholder="Start address (shop, yard, etc.)"
                            aria-label={`${c.name} start address`}
                            value={endpoints[c.name]?.start_address ?? ""}
                            onChange={(e) =>
                              setEndpoints((prev) => ({
                                ...prev,
                                [c.name]: {
                                  ...(prev[c.name] ?? EMPTY_ENDPOINT),
                                  start_address: e.target.value,
                                  start_lat: null,
                                  start_lng: null,
                                },
                              }))

                            }
                            onBlur={(e) =>
                              saveEndpoint(c.name, "start_address", e.target.value)
                            }
                            disabled={savingEndpoint === `${c.name}:start_address`}
                          />
                        ) : (
                          <span className="text-muted-foreground">
                            {endpoints[c.name]?.start_address || "—"}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>

                  {Array.from({ length: maxStops }).map((_, row) => (
                    <tr
                      key={row}
                      className="border-t border-border even:bg-muted/40 [&>td]:border-r [&>td]:border-border [&>td]:px-2 [&>td]:py-1 [&>td]:align-top [&>td:last-child]:border-r-0"
                    >
                      <td className="text-muted-foreground">{row + 1}</td>
                      {crewColumns.map((c) => {
                        const job = c.jobs[row];
                        if (!job)
                          return (
                            <td key={c.name} className="text-muted-foreground">
                              —
                            </td>
                          );
                        return (
                          <td key={c.name}>
                            <div className="font-medium">{job.customer_name}</div>
                            <div className="text-muted-foreground">{job.address}</div>
                            <div className="mt-1 flex flex-wrap items-center gap-1">
                              {(() => {
                                const d = job.customer_id
                                  ? durationByCustomer.get(job.customer_id)
                                  : undefined;
                                const minutes = d?.minutes ?? 45;
                                return (
                                  <Badge variant="outline" className="text-[10px]">
                                    {d?.actual ? "avg" : "est"} {minutes} min
                                  </Badge>
                                );
                              })()}
                              <Badge
                                variant={STATUS_VARIANT[job.status] ?? "outline"}
                                className="text-[10px]"
                              >
                                {STATUS_LABEL[job.status] ?? LEGACY_STATUS_LABEL[job.status] ?? job.status}
                              </Badge>
                              {(job.notes ?? "").startsWith(CATCH_UP_NOTE) && (
                                <Badge className="border-amber-500 bg-amber-500/15 text-[10px] text-amber-700 hover:bg-amber-500/15">
                                  Catch-up · missed
                                </Badge>
                              )}
                            </div>
                            {job.completion_photo_path && (
                              <button
                                type="button"
                                onClick={() => openProof(job)}
                                className="mt-0.5 text-[10px] font-medium text-primary underline"
                              >
                                View proof photo
                              </button>
                            )}
                            {job.skip_reason && (
                              <div className="mt-0.5 text-[10px] text-destructive">
                                Skipped: {job.skip_reason}
                              </div>
                            )}
                            {isOwner && (
                              <>
                                <Select
                                  value={job.status}
                                  onValueChange={(v) => updateJob(job.id, { status: v })}
                                >
                                  <SelectTrigger
                                    className="mt-1 h-7 w-[130px] text-xs"
                                    aria-label="Job status"
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-popover">
                                    {Object.entries(STATUS_LABEL).map(([v, label]) => (
                                      <SelectItem key={v} value={v}>
                                        {label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <div className="mt-1 flex flex-wrap items-center gap-1">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-6 w-6"
                                    aria-label={`Move ${job.customer_name} earlier`}
                                    disabled={row === 0}
                                    onClick={() => reorderStop(job, -1)}
                                  >
                                    <ChevronUp className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-6 w-6"
                                    aria-label={`Move ${job.customer_name} later`}
                                    disabled={row >= c.jobs.length - 1}
                                    onClick={() => reorderStop(job, 1)}
                                  >
                                    <ChevronDown className="h-3.5 w-3.5" />
                                  </Button>
                                  <Select
                                    value={job.crew ?? "Unassigned"}
                                    onValueChange={(v) => moveStopToCrew(job, v)}
                                  >
                                    <SelectTrigger
                                      className="h-6 w-[86px] px-1 text-[11px]"
                                      aria-label={`Move ${job.customer_name} to another crew`}
                                    >
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-popover">
                                      <SelectItem value="Unassigned">Unassigned</SelectItem>
                                      {activeCrewNames.map((n) => (
                                        <SelectItem key={n} value={n}>
                                          {n}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 px-1 text-[11px]"
                                    onClick={() => skipStop(job)}
                                  >
                                    Skip
                                  </Button>
                                </div>
                              </>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr className="border-t border-border bg-primary/5 [&>td]:border-r [&>td]:border-border [&>td]:px-2 [&>td]:py-1 [&>td]:align-top [&>td:last-child]:border-r-0">
                    <td className="font-semibold text-muted-foreground">End</td>
                    {crewColumns.map((c) => (
                      <td key={c.name}>
                        {isOwner && c.name !== "Unassigned" ? (
                          <Input
                            className="h-7 text-xs"
                            placeholder="End address (defaults to start)"
                            aria-label={`${c.name} end address`}
                            value={endpoints[c.name]?.end_address ?? ""}
                            onChange={(e) =>
                              setEndpoints((prev) => ({
                                ...prev,
                                [c.name]: {
                                  ...(prev[c.name] ?? EMPTY_ENDPOINT),
                                  end_address: e.target.value,
                                  end_lat: null,
                                  end_lng: null,
                                },
                              }))

                            }
                            onBlur={(e) => saveEndpoint(c.name, "end_address", e.target.value)}
                            disabled={savingEndpoint === `${c.name}:end_address`}
                          />
                        ) : (
                          <span className="text-muted-foreground">
                            {endpoints[c.name]?.end_address || "—"}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-t-2 border-border bg-muted/60 [&>td]:border-r [&>td]:border-border [&>td]:px-2 [&>td]:py-1 [&>td]:align-top [&>td:last-child]:border-r-0">
                    <td className="font-semibold text-muted-foreground">Day total</td>
                    {crewColumns.map((c) => {
                      const drive = driveByCrew[c.name];
                      const work = c.jobs.reduce(
                        (sum, j) =>
                          sum +
                          (j.customer_id ? durationByCustomer.get(j.customer_id)?.minutes ?? 45 : 45),
                        0
                      );
                      const driveMin = drive?.minutes ?? null;
                      const total = driveMin != null ? work + driveMin : null;
                      return (
                        <td key={c.name} className="text-[11px] leading-tight">
                          <div className="font-semibold">
                            {total != null ? `${fmtHrs(total)} total` : `${fmtHrs(work)} work`}
                          </div>
                          <div className="text-muted-foreground">
                            {fmtHrs(work)} work
                            {driveMin != null ? ` + ${fmtHrs(driveMin)} drive` : " · drive n/a"}
                            {drive?.miles != null ? ` · ${drive.miles.toFixed(1)} mi` : ""}
                          </div>
                          {driveMin == null && (
                            <div className="text-muted-foreground">Optimize to add drive time</div>
                          )}
                          {(() => {
                            const doneCount = c.jobs.filter((j) => j.status.startsWith("complete")).length;
                            if (dateFilter !== todayISO() || c.jobs.length === 0) {
                              return doneCount > 0 ? (
                                <div className="text-muted-foreground">
                                  {doneCount}/{c.jobs.length} done
                                </div>
                              ) : null;
                            }
                            const now = new Date();
                            const elapsed = now.getHours() * 60 + now.getMinutes() - 8 * 60;
                            const span = total ?? work;
                            const expected =
                              elapsed <= 0 ? 0 : Math.min(c.jobs.length, (elapsed / span) * c.jobs.length);
                            const behind = doneCount + 0.5 < expected;
                            return (
                              <div className={behind ? "font-medium text-destructive" : "text-primary"}>
                                {doneCount}/{c.jobs.length} done
                                {behind ? ` · behind by ~${Math.round(expected - doneCount)} stop(s)` : " · on pace"}
                              </div>
                            );
                          })()}
                          {dayTargetMinutes != null && (
                            <div
                              className={
                                (total ?? work) > dayTargetMinutes
                                  ? "font-medium text-destructive"
                                  : "font-medium text-primary"
                              }
                            >
                              Target {fmtHrs(dayTargetMinutes)} ·{" "}
                              {(total ?? work) > dayTargetMinutes
                                ? `${fmtHrs((total ?? work) - dayTargetMinutes)} over`
                                : `${fmtHrs(dayTargetMinutes - (total ?? work))} left`}
                            </div>
                          )}

                        </td>
                      );
                    })}
                  </tr>
                </tbody>



              </table>
            </div>
            <CrewRouteMap
              crews={crewColumns.map((c) => ({
                name: c.name,
                jobs: c.jobs.map((j) => ({
                  ...j,
                  catchUp: (j.notes ?? "").startsWith(CATCH_UP_NOTE),
                })),
                start: endpoints[c.name]?.start_lat != null && endpoints[c.name]?.start_lng != null
                  ? {
                      lat: endpoints[c.name].start_lat as number,
                      lng: endpoints[c.name].start_lng as number,
                      address: endpoints[c.name].start_address,
                    }
                  : null,
                end: endpoints[c.name]?.end_lat != null && endpoints[c.name]?.end_lng != null
                  ? {
                      lat: endpoints[c.name].end_lat as number,
                      lng: endpoints[c.name].end_lng as number,
                      address: endpoints[c.name].end_address || endpoints[c.name].start_address,
                    }
                  : null,
              }))}
            />

          </div>
        )}

      </CardContent>
    </Card>
  );
}
